import React, { useState, useEffect } from 'react';
import { Download, Trash2, Mail, Calendar, ArrowLeft, Search } from 'lucide-react';
import { emailStorage } from '../utils/emailStorage';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const Admin = () => {
  const [emails, setEmails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEmails, setFilteredEmails] = useState([]);

  useEffect(() => {
    loadEmails();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = emails.filter(entry => 
        entry.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmails(filtered);
    } else {
      setFilteredEmails(emails);
    }
  }, [searchTerm, emails]);

  const loadEmails = () => {
    const data = emailStorage.getAll();
    setEmails(data);
    setFilteredEmails(data);
  };

  const handleExportCSV = () => {
    const csv = emailStorage.exportCSV();
    if (!csv) {
      toast.error('No emails to export');
      return;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gryp-gear-emails-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Emails exported successfully!');
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this email?')) {
      const result = emailStorage.remove(id);
      if (result.success) {
        toast.success('Email deleted');
        loadEmails();
      } else {
        toast.error('Failed to delete email');
      }
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to delete ALL emails? This cannot be undone.')) {
      const result = emailStorage.clear();
      if (result.success) {
        toast.success('All emails cleared');
        loadEmails();
      } else {
        toast.error('Failed to clear emails');
      }
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className="min-h-screen bg-slate-950 text-slate-100 py-12 px-6"
      data-component-path="client/src/pages/Admin.jsx"
      data-component-line="1:5"
      data-component-id="client/src/pages/Admin.jsx:1:5"
    >
      <div 
        className="max-w-6xl mx-auto"
        data-component-path="client/src/pages/Admin.jsx"
        data-component-line="2:7"
        data-component-id="client/src/pages/Admin.jsx:2:7"
      >
        {/* Header */}
        <div 
          className="mb-8"
          data-component-path="client/src/pages/Admin.jsx"
          data-component-line="3:9"
          data-component-id="client/src/pages/Admin.jsx:3:9"
        >
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors mb-6"
            data-component-path="client/src/pages/Admin.jsx"
            data-component-line="4:11"
            data-component-id="client/src/pages/Admin.jsx:4:11"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
          
          <div 
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            data-component-path="client/src/pages/Admin.jsx"
            data-component-line="5:11"
            data-component-id="client/src/pages/Admin.jsx:5:11"
          >
            <div>
              <h1 
                className="text-4xl font-bold text-slate-100 mb-2"
                data-component-path="client/src/pages/Admin.jsx"
                data-component-line="6:13"
                data-component-id="client/src/pages/Admin.jsx:6:13"
              >
                Email Waitlist
              </h1>
              <p 
                className="text-slate-400"
                data-component-path="client/src/pages/Admin.jsx"
                data-component-line="7:13"
                data-component-id="client/src/pages/Admin.jsx:7:13"
              >
                Manage your grYp Gear launch subscribers
              </p>
            </div>

            <div 
              className="flex items-center gap-3"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="8:13"
              data-component-id="client/src/pages/Admin.jsx:8:13"
            >
              <button
                onClick={handleExportCSV}
                disabled={emails.length === 0}
                className="flex items-center gap-2 h-11 px-6 bg-slate-700 text-slate-100 font-medium rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-component-path="client/src/pages/Admin.jsx"
                data-component-line="9:15"
                data-component-id="client/src/pages/Admin.jsx:9:15"
              >
                <Download className="w-5 h-5" />
                Export CSV
              </button>
              
              <button
                onClick={handleClearAll}
                disabled={emails.length === 0}
                className="flex items-center gap-2 h-11 px-6 bg-red-900/20 text-red-400 font-medium rounded hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all border border-red-900/50"
                data-component-path="client/src/pages/Admin.jsx"
                data-component-line="10:15"
                data-component-id="client/src/pages/Admin.jsx:10:15"
              >
                <Trash2 className="w-5 h-5" />
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div 
          className="grid md:grid-cols-3 gap-6 mb-8"
          data-component-path="client/src/pages/Admin.jsx"
          data-component-line="11:9"
          data-component-id="client/src/pages/Admin.jsx:11:9"
        >
          <div 
            className="bg-slate-900 border border-slate-800 rounded-lg p-6"
            data-component-path="client/src/pages/Admin.jsx"
            data-component-line="12:11"
            data-component-id="client/src/pages/Admin.jsx:12:11"
          >
            <div 
              className="flex items-center gap-3 mb-2"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="13:13"
              data-component-id="client/src/pages/Admin.jsx:13:13"
            >
              <Mail className="w-6 h-6 text-slate-400" />
              <span className="text-slate-400 font-medium">Total Subscribers</span>
            </div>
            <p 
              className="text-4xl font-bold text-slate-100"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="14:13"
              data-component-id="client/src/pages/Admin.jsx:14:13"
            >
              {emails.length}
            </p>
          </div>

          <div 
            className="bg-slate-900 border border-slate-800 rounded-lg p-6"
            data-component-path="client/src/pages/Admin.jsx"
            data-component-line="15:11"
            data-component-id="client/src/pages/Admin.jsx:15:11"
          >
            <div 
              className="flex items-center gap-3 mb-2"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="16:13"
              data-component-id="client/src/pages/Admin.jsx:16:13"
            >
              <Calendar className="w-6 h-6 text-slate-400" />
              <span className="text-slate-400 font-medium">Latest Signup</span>
            </div>
            <p 
              className="text-lg font-semibold text-slate-100"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="17:13"
              data-component-id="client/src/pages/Admin.jsx:17:13"
            >
              {emails.length > 0 ? formatDate(emails[emails.length - 1].timestamp) : 'No signups yet'}
            </p>
          </div>

          <div 
            className="bg-slate-900 border border-slate-800 rounded-lg p-6"
            data-component-path="client/src/pages/Admin.jsx"
            data-component-line="18:11"
            data-component-id="client/src/pages/Admin.jsx:18:11"
          >
            <div 
              className="flex items-center gap-3 mb-2"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="19:13"
              data-component-id="client/src/pages/Admin.jsx:19:13"
            >
              <Search className="w-6 h-6 text-slate-400" />
              <span className="text-slate-400 font-medium">Filtered Results</span>
            </div>
            <p 
              className="text-4xl font-bold text-slate-100"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="20:13"
              data-component-id="client/src/pages/Admin.jsx:20:13"
            >
              {filteredEmails.length}
            </p>
          </div>
        </div>

        {/* Search */}
        <div 
          className="mb-6"
          data-component-path="client/src/pages/Admin.jsx"
          data-component-line="21:9"
          data-component-id="client/src/pages/Admin.jsx:21:9"
        >
          <div 
            className="relative"
            data-component-path="client/src/pages/Admin.jsx"
            data-component-line="22:11"
            data-component-id="client/src/pages/Admin.jsx:22:11"
          >
            <Search 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="23:13"
              data-component-id="client/src/pages/Admin.jsx:23:13"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search emails..."
              className="w-full h-12 pl-12 pr-4 bg-slate-900 border border-slate-800 text-slate-100 placeholder-slate-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="24:13"
              data-component-id="client/src/pages/Admin.jsx:24:13"
            />
          </div>
        </div>

        {/* Email List */}
        <div 
          className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden"
          data-component-path="client/src/pages/Admin.jsx"
          data-component-line="25:9"
          data-component-id="client/src/pages/Admin.jsx:25:9"
        >
          {filteredEmails.length === 0 ? (
            <div 
              className="p-12 text-center"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="26:11"
              data-component-id="client/src/pages/Admin.jsx:26:11"
            >
              <Mail 
                className="w-16 h-16 mx-auto text-slate-700 mb-4"
                data-component-path="client/src/pages/Admin.jsx"
                data-component-line="27:13"
                data-component-id="client/src/pages/Admin.jsx:27:13"
              />
              <p 
                className="text-slate-400 text-lg"
                data-component-path="client/src/pages/Admin.jsx"
                data-component-line="28:13"
                data-component-id="client/src/pages/Admin.jsx:28:13"
              >
                {searchTerm ? 'No emails match your search' : 'No emails collected yet'}
              </p>
            </div>
          ) : (
            <div 
              className="overflow-x-auto"
              data-component-path="client/src/pages/Admin.jsx"
              data-component-line="29:11"
              data-component-id="client/src/pages/Admin.jsx:29:11"
            >
              <table 
                className="w-full"
                data-component-path="client/src/pages/Admin.jsx"
                data-component-line="30:13"
                data-component-id="client/src/pages/Admin.jsx:30:13"
              >
                <thead 
                  className="bg-slate-800 border-b border-slate-700"
                  data-component-path="client/src/pages/Admin.jsx"
                  data-component-line="31:15"
                  data-component-id="client/src/pages/Admin.jsx:31:15"
                >
                  <tr>
                    <th 
                      className="text-left py-4 px-6 text-slate-300 font-semibold"
                      data-component-path="client/src/pages/Admin.jsx"
                      data-component-line="32:17"
                      data-component-id="client/src/pages/Admin.jsx:32:17"
                    >
                      Email
                    </th>
                    <th 
                      className="text-left py-4 px-6 text-slate-300 font-semibold"
                      data-component-path="client/src/pages/Admin.jsx"
                      data-component-line="33:17"
                      data-component-id="client/src/pages/Admin.jsx:33:17"
                    >
                      Signup Date
                    </th>
                    <th 
                      className="text-right py-4 px-6 text-slate-300 font-semibold"
                      data-component-path="client/src/pages/Admin.jsx"
                      data-component-line="34:17"
                      data-component-id="client/src/pages/Admin.jsx:34:17"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmails.map((entry, index) => (
                    <tr 
                      key={entry.id}
                      className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors"
                      data-component-path="client/src/pages/Admin.jsx"
                      data-component-line="35:17"
                      data-component-id="client/src/pages/Admin.jsx:35:17"
                    >
                      <td 
                        className="py-4 px-6 text-slate-100 font-medium"
                        data-component-path="client/src/pages/Admin.jsx"
                        data-component-line="36:19"
                        data-component-id="client/src/pages/Admin.jsx:36:19"
                      >
                        {entry.email}
                      </td>
                      <td 
                        className="py-4 px-6 text-slate-400"
                        data-component-path="client/src/pages/Admin.jsx"
                        data-component-line="37:19"
                        data-component-id="client/src/pages/Admin.jsx:37:19"
                      >
                        {formatDate(entry.timestamp)}
                      </td>
                      <td 
                        className="py-4 px-6 text-right"
                        data-component-path="client/src/pages/Admin.jsx"
                        data-component-line="38:19"
                        data-component-id="client/src/pages/Admin.jsx:38:19"
                      >
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="inline-flex items-center gap-2 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-all"
                          data-component-path="client/src/pages/Admin.jsx"
                          data-component-line="39:21"
                          data-component-id="client/src/pages/Admin.jsx:39:21"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Note */}
        <div 
          className="mt-8 p-6 bg-blue-900/20 border border-blue-900/50 rounded-lg"
          data-component-path="client/src/pages/Admin.jsx"
          data-component-line="40:9"
          data-component-id="client/src/pages/Admin.jsx:40:9"
        >
          <p 
            className="text-blue-300 text-sm"
            data-component-path="client/src/pages/Admin.jsx"
            data-component-line="41:11"
            data-component-id="client/src/pages/Admin.jsx:41:11"
          >
            <strong>Note:</strong> Emails are currently stored in browser localStorage (mock backend). 
            To implement real backend storage, replace the emailStorage utility with API calls to your server.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Admin;